import Vue from 'vue'
import Router from 'vue-router'

import { toV1 } from './routesToV1'

// Containers
import DefaultContainer from '@/containers/DefaultContainer'

// Views
import Insights, {
    ParentlessObjectives,
    UserWithoutOKRs,
    LateKeyResults,
    ObjectivesWithMoreThanFiveKRs,
    ObjectivesWithLessThanTwoKRs,
} from '@/views/insights'

import Kpis, { List as KpiList, New as KpiNew, Edit as KpiEdit, Kpi as KpiView } from '@/views/target/kpis'

import Targets, {
    List as TargetsList,
    New as TargetNew,
    Edit as TargetEdit,
    Target as TargetView,
    LinkTags,
    SuggestGoal,
    FocalPoint,
    BulkEditing,
} from '@/views/target/targets'

import { AutomaticCalculation } from '@/views/target/components/view/tab-content/settings'

import { Expectation } from '@/views/target/components/view'

import Profile from '@/views/profile'

import Ranges, { List as RangesList, New as RangeNew, Edit as RangeEdit } from '@/views/ranges'

import Enps, { NewEnpsResearch, EditEnpsResearch, Report as EnpsReport, Duplicate } from '@/views/enps'

import UnitsOfMeasurement, {
    List as UnitsOfMeasurementList,
    New as UnitOfMeasurementNew,
    Edit as UnitOfMeasurementEdit,
} from '@/views/units-of-measurement'

import MoodPulse, { QuizReport } from '@/views/mood-pulse'

import PDI, {
    New as PdiNew,
    Edit as PdiEdit,
    NewActionPlan,
    EditActionPlan,
    PdiCheckin,
    ActionPlanDetails,
} from '@/views/pdi'

import {
    NewFormation,
    NewLanguage,
    NewCareerInTheCompany,
    NewCareerInTheMarket,
    EditFormation,
    EditLanguage,
    EditCareerInTheCompany,
    EditCareerInTheMarket,
} from '@/views/mini_curriculum'

import Committee, { List as CommitteeList, New as CommitteeNew, Edit as CommitteeEdit } from '@/views/committee'

import GeneralComment, { New as GeneralCommentNew, Edit as GeneralCommentEdit } from '@/views/general-comment'

import People, {
    List as PeopleList,
    UpdateUsersNetworks,
    ClearUsersNetwork,
    UsersStagesAdministration,
    UploadUsersModal as UploadUsers,
    UploadsModal,
    UploadPdisModal as UploadPdis,
    UploadFeedbacksModal as UploadFeedbacks,
    CreateModal as CreateFilterModal,
    UsersBulkEditModal as BulkEditModal,
    UsersBindCommitteeModal as BindCommitteeModal,
} from '@/views/people'

import Strategy, { Onepage } from '@/views/strategy'

import CheckIn, { CheckInSimulated, CheckInProjection } from '@/views/check-in'

import { RequestFeedbackModal, SendFeedbackModal } from '@/views/profile/components/talk'

import Home from '@/views/home'

import Dashboard from '@/views/dashboard'

import NBox, { List as NBoxList, New as NBoxNew, Edit as NBoxEdit, Report as NBoxReport } from '@/views/n-box'

import Values, {
    UserNetworks,
    SendSpontaneousEvaluationModal,
    Evaluations as EvaluationsList,
    EvaluationsReport,
} from '@/views/values'

import OKR from '@/views/okr'

import AllOkrs from '@/views/okr/all-okrs'

import { Edit as OKRObjectiveEdit } from '@/views/okr/objective'

import { Collaboration as OKRObjectiveCollaboration } from '@/views/okr/objective'

import { Duplicate as OKRObjectiveDuplicate } from '@/views/okr/objective'

import { Edit as OKRKeyResultEdit } from '@/views/okr/key-result'

import { Move as OKRKeyResultMove } from '@/views/okr/key-result'

import { New as PlanNew, Edit as PlanEdit } from '@/views/career-objectives'

import TagReport from '@/views/tags-report'

import TermsOfUse from '@/views/terms-of-use'

// Auth
import { Error, Login, LoginCognitoAD, ForgotPassword, CreatePassword } from '@/views/pages'

import i18n from '@/i18n'

import { List as TemplatesList, New as NewTemplate, Edit as EditTemplate } from '@/views/talk/templates'

import Feedbacks, { List as FeedbacksList, RequestAdminFeedbackModal } from '@/views/talk/feedbacks'

import { shallPass } from './NavigationGuard'

import tokenStorage from '@/services/token/storage'

Vue.use(Router)

const router = new Router({
    mode: 'history',
    linkActiveClass: 'open active',
    routes: [
        {
            path: '/',
            name: 'Home',
            redirect: '/v2/home',
            component: DefaultContainer,
            children: [
                {
                    path: 'v2/home',
                    name: 'Home',
                    component: Home,
                },
                {
                    path: 'v2/home-okr',
                    name: 'Home',
                    component: () => toV1('/home-okr'),
                },
                {
                    path: 'v2/action-plan/view/:actionPlanId',
                    name: i18n.t('pdis.actionPlan'),
                    component: ActionPlanDetails,
                    children: [
                        {
                            path: 'checkin',
                            name: i18n.t('checkIn.checkIn'),
                            component: PdiCheckin,
                        },
                    ],
                },
                {
                    path: 'v2/insights',
                    name: 'Insights',
                    component: Insights,
                    children: [
                        {
                            path: 'users-without-okrs',
                            name: i18n.t('insights.usersWithoutOKRs'),
                            component: UserWithoutOKRs,
                        },
                        {
                            path: 'late-key-results/:periods?',
                            name: i18n.t('insights.objectivesMisaligned'),
                            component: LateKeyResults,
                        },
                        {
                            path: 'parentless-objectives/:periods?',
                            name: i18n.t('insights.objectivesMisaligned'),
                            component: ParentlessObjectives,
                        },
                        {
                            path: 'objectives-with-more-than-5-krs',
                            name: i18n.t('insights.objectivesWithMoreThan5KRs'),
                            component: ObjectivesWithMoreThanFiveKRs,
                        },
                        {
                            path: 'objectives-less-than-2-krs',
                            name: i18n.t('insights.objectivesWithLessThan2KRs'),
                            component: ObjectivesWithLessThanTwoKRs,
                        },
                    ],
                },
                {
                    name: i18n.t('reports.reports'),
                    path: 'v2/reports',
                    redirect: 'v2/reports/list',
                    component: () => import('@/views/reports'),
                    beforeEnter: (to, from, next) => {
                        return shallPass(next, true)
                    },
                    children: [
                        {
                            path: 'list',
                            component: () => import('@/views/reports/children/list'),
                        },
                        {
                            name: i18n.t('new'),
                            path: 'new',
                            component: () => import('@/views/reports/children/new'),
                        },
                        {
                            name: i18n.t('edit'),
                            path: 'edit/:id',
                            component: () => import('@/views/reports/children/edit'),
                        },
                        {
                            name: 'Dashboard',
                            path: 'view/:id',
                            component: () => import('@/views/reports/children/view'),
                        },
                    ],
                },
                {
                    path: 'v2/dashboard',
                    name: 'Dashboard',
                    component: Dashboard,
                },
                {
                    path: 'v2/people',
                    name: 'People',
                    redirect: 'v2/people/list',
                    component: People,
                    children: [
                        {
                            path: 'list',
                            name: i18n.t('list'),
                            component: PeopleList,
                            children: [
                                {
                                    path: 'users-stages-administration',
                                    name: 'UsersStagesAdministration',
                                    component: UsersStagesAdministration,
                                },
                                {
                                    path: 'update-users-networks',
                                    name: 'UpdateUsersNetworks',
                                    component: UpdateUsersNetworks,
                                },
                                {
                                    path: 'clear-users-network',
                                    name: 'ClearUsersNetwork',
                                    component: ClearUsersNetwork,
                                },
                                {
                                    path: 'uploads',
                                    name: 'Uploads',
                                    component: UploadsModal,
                                },
                                {
                                    path: 'upload-users',
                                    name: 'UploadUsers',
                                    component: UploadUsers,
                                },
                                {
                                    path: 'upload-pdis',
                                    name: 'UploadPdis',
                                    component: UploadPdis,
                                },
                                {
                                    path: 'upload-feedbacks',
                                    name: 'UploadFeedbacks',
                                    component: UploadFeedbacks,
                                },
                                {
                                    path: 'bulk-edit',
                                    name: 'BulkEdit',
                                    component: BulkEditModal,
                                },
                                {
                                    path: 'bind-committee',
                                    name: 'BindCommittee',
                                    component: BindCommitteeModal,
                                },
                                {
                                    path: 'create-filter',
                                    name: i18n.t('people.filters.create.title'),
                                    component: CreateFilterModal,
                                },
                                {
                                    path: 'edit-filter/:id',
                                    name: i18n.t('people.filters.edit.title'),
                                    component: CreateFilterModal,
                                },
                            ],
                        },
                        {
                            path: 'performance',
                            component: () => toV1('/people/performance'),
                        },
                    ],
                },
                {
                    path: 'v2/strategy',
                    name: i18n.t('strategy.name'),
                    component: Strategy,
                    redirect: 'v2/strategy/onepage',
                    children: [
                        {
                            path: 'onepage',
                            name: i18n.t('strategy.onepage.title'),
                            component: Onepage,
                            children: [
                                {
                                    path: 'compact-mode',
                                    name: i18n.t('strategy.compactMode.title'),
                                    meta: { compactMode: true },
                                    component: () => import('@/views/strategy/onepage/children/compact-mode'),
                                },
                                {
                                    path: 'setup-horizons',
                                    name: i18n.t('strategy.onepage.horizons.title'),
                                    component: () => import('@/views/strategy/onepage/children/setup-horizons'),
                                },
                                {
                                    path: 'permission-access',
                                    name: i18n.t('strategy.onepage.permissionAccess.title'),
                                    component: () => import('@/views/strategy/onepage/children/permission-access'),
                                },
                            ],
                        },
                        {
                            path: 'link-okr',
                            name: i18n.t('strategy.okrs.link'),
                            component: () => import('@/views/strategy/link-okr'),
                            redirect: 'link-okr/list',
                            children: [
                                {
                                    path: 'list',
                                    name: i18n.t('strategy.okrs.list'),
                                    component: () => import('@/views/strategy/link-okr/children/List'),
                                    children: [
                                        {
                                            path: 'objectiveId/:objectiveId/resultId/:resultId/checkin',
                                            name: 'Check-In',
                                            component: () => import('@/views/okr/check-in'),
                                        },
                                        {
                                            path: 'objectiveId/:objectiveId/resultId/:resultId/edit',
                                            name: i18n.t('okr.editKeyResult'),
                                            component: OKRKeyResultEdit,
                                        },
                                        {
                                            path: 'objective/:objectiveId/edit',
                                            name: i18n.t('objectives.editObjective'),
                                            component: OKRObjectiveEdit,
                                        },
                                    ],
                                },
                                {
                                    path: 'linked',
                                    name: i18n.tc('strategy.okrs.linked', 2),
                                    component: () => import('@/views/strategy/link-okr/children/Linked'),
                                    children: [
                                        {
                                            path: 'objectiveId/:objectiveId/resultId/:resultId/checkin',
                                            name: 'Check-In',
                                            component: () => import('@/views/okr/check-in'),
                                        },
                                        {
                                            path: 'objectiveId/:objectiveId/resultId/:resultId/edit',
                                            name: i18n.t('okr.editKeyResult'),
                                            component: OKRKeyResultEdit,
                                        },
                                        {
                                            path: 'objective/:objectiveId/edit',
                                            name: i18n.t('objectives.editObjective'),
                                            component: OKRObjectiveEdit,
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                {
                    path: 'v2/mini-curriculum/formations/new',
                    name: i18n.t('new'),
                    component: NewFormation,
                },
                {
                    path: 'v2/mini-curriculum/formations/:id/edit',
                    name: i18n.t('edit'),
                    component: EditFormation,
                },
                {
                    path: 'v2/mini-curriculum/languages/new',
                    name: i18n.t('new'),
                    component: NewLanguage,
                },
                {
                    path: 'v2/mini-curriculum/languages/:id/edit',
                    name: i18n.t('edit'),
                    component: EditLanguage,
                },
                {
                    path: 'v2/mini-curriculum/career-in-the-company/new',
                    name: i18n.t('new'),
                    component: NewCareerInTheCompany,
                },
                {
                    path: 'v2/mini-curriculum/career-in-the-company/:id/edit',
                    name: i18n.t('edit'),
                    component: EditCareerInTheCompany,
                },
                {
                    path: 'v2/mini-curriculum/career-in-the-market/new',
                    name: i18n.t('new'),
                    component: NewCareerInTheMarket,
                },
                {
                    path: 'v2/mini-curriculum/career-in-the-market/:id/edit',
                    name: i18n.t('edit'),
                    component: EditCareerInTheMarket,
                },
                {
                    path: 'v2/profile/:id',
                    name: i18n.t('profile.profile'),
                    component: Profile,
                    children: [
                        {
                            path: 'spontaneous-evaluation',
                            name: i18n.t('values.spontaneousEvaluation'),
                            component: SendSpontaneousEvaluationModal,
                        },
                        {
                            path: 'pdi/:pdiId/action-plan/:actionPlanId/checkin',
                            name: i18n.t('checkIn.checkIn'),
                            component: PdiCheckin,
                        },
                        {
                            path: 'goal/:targetId/checkin',
                            name: i18n.t('checkIn.checkIn'),
                            component: CheckIn,
                        },
                        {
                            path: 'goal/:targetId/checkin/:checkinId',
                            name: i18n.t('checkIn.checkIn'),
                            component: CheckIn,
                        },
                        {
                            path: 'feedbacks/request',
                            name: i18n.t('profile.feedback.request'),
                            component: RequestFeedbackModal,
                        },
                        {
                            path: 'feedbacks/send',
                            name: i18n.t('profile.feedback.send'),
                            component: SendFeedbackModal,
                        },
                        {
                            path: 'objective/:objectiveId/edit',
                            name: i18n.t('objectives.editObjective'),
                            component: OKRObjectiveEdit,
                        },
                        {
                            path: 'objective/:objectiveId/duplicate',
                            name: i18n.t('okr.modal.objective.duplicate'),
                            component: OKRObjectiveDuplicate,
                        },
                        {
                            path: 'objectiveId/:objectiveId/resultId/:resultId/checkin',
                            name: 'Check-In',
                            component: () => import('@/views/okr/check-in'),
                        },
                        {
                            path: 'objectiveId/:objectiveId/resultId/:resultId/edit',
                            name: i18n.t('okr.editKeyResult'),
                            component: OKRKeyResultEdit,
                        },
                        {
                            path: 'objectiveId/:objectiveId/resultId/:resultId/move',
                            name: i18n.t('okr.moveKeyResult'),
                            component: OKRKeyResultMove,
                        },
                        {
                            path: 'objective/:objectiveId/request',
                            name: i18n.t('objectives.editObjective'),
                            component: OKRObjectiveCollaboration,
                        },
                    ],
                },
                {
                    path: 'v2/career-objectives',
                    name: i18n.t('careerObjective.careerObjective'),
                    redirect: 'v2/career-objectives/plan/new',
                    component: {
                        render(c) {
                            return c('router-view')
                        },
                    },
                    children: [
                        {
                            path: 'plan',
                            name: i18n.t('careerObjective.careerObjective'),
                            redirect: '/v2/career-objectives/plan/new',
                            component: {
                                render(c) {
                                    return c('router-view')
                                },
                            },
                            children: [
                                {
                                    path: 'new',
                                    name: i18n.t('new'),
                                    component: PlanNew,
                                },
                                {
                                    path: 'edit/:id',
                                    name: i18n.t('edit'),
                                    component: PlanEdit,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: 'v2/general-comment',
                    name: 'General comment',
                    redirect: 'v2/general-comment/new',
                    component: GeneralComment,
                    children: [
                        {
                            path: 'new',
                            name: i18n.t('new'),
                            component: GeneralCommentNew,
                        },
                        {
                            path: 'edit/:id',
                            name: i18n.t('edit'),
                            component: GeneralCommentEdit,
                        },
                    ],
                },
                {
                    path: 'v2/target',
                    name: 'Target',
                    redirect: 'v2/target/targets',
                    component: {
                        render(c) {
                            return c('router-view')
                        },
                    },
                    children: [
                        {
                            path: 'pdi',
                            name: i18n.t('profile.tabs.pdi'),
                            redirect: 'pdi/list',
                            component: PDI,
                            children: [
                                {
                                    path: 'new',
                                    name: i18n.t('new'),
                                    component: PdiNew,
                                },
                                {
                                    path: 'edit/:id',
                                    name: i18n.t('edit'),
                                    component: PdiEdit,
                                },
                                {
                                    path: ':pdiId/new-action-plan',
                                    name: i18n.t('new'),
                                    component: NewActionPlan,
                                },
                                {
                                    path: ':pdiId/edit-action-plan/:actionPlanId',
                                    name: i18n.t('edit'),
                                    component: EditActionPlan,
                                },
                            ],
                        },
                        {
                            path: 'indicators',
                            name: i18n.t('indicators.indicators'),
                            redirect: 'indicators/list',
                            component: Kpis,
                            children: [
                                {
                                    path: 'list',
                                    name: i18n.t('list'),
                                    component: KpiList,
                                },
                                {
                                    path: 'new',
                                    name: i18n.t('new'),
                                    component: KpiNew,
                                },
                                {
                                    path: 'edit/:id',
                                    name: i18n.t('edit'),
                                    component: KpiEdit,
                                },
                                {
                                    path: 'view/:kpiId',
                                    name: i18n.t('targets.view'),
                                    component: KpiView,
                                    children: [
                                        {
                                            path: 'checkin',
                                            name: i18n.t('checkIn.checkIn'),
                                            component: CheckIn,
                                        },
                                        {
                                            path: 'checkin/:checkinId',
                                            name: i18n.t('checkIn.checkIn'),
                                            component: CheckIn,
                                        },
                                        {
                                            path: 'checkin-simulated',
                                            name: i18n.t('checkIn.checkIn'),
                                            component: CheckInSimulated,
                                        },
                                        {
                                            path: 'retroactive/:expectationId',
                                            name: i18n.t('checkIn.retroactive'),
                                            component: CheckIn,
                                        },
                                        {
                                            path: 'expectation',
                                            name: i18n.t('targets.expectation'),
                                            component: Expectation,
                                        },
                                        {
                                            path: 'automatic-calculation',
                                            name: i18n.t('targets.automaticCalculation'),
                                            component: AutomaticCalculation,
                                        },
                                        {
                                            path: 'checkin-projection/:checkinId',
                                            name: i18n.t('targets.checkIn'),
                                            component: CheckInProjection,
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: 'targets',
                            name: i18n.t('targets.targets'),
                            redirect: 'targets/list',
                            component: Targets,
                            children: [
                                {
                                    path: 'list',
                                    name: i18n.t('list'),
                                    component: TargetsList,
                                    children: [
                                        {
                                            path: 'bulk-editing',
                                            name: i18n.t('targets.bulkEditing'),
                                            component: BulkEditing,
                                        },
                                    ],
                                },
                                {
                                    path: 'new',
                                    name: i18n.t('new'),
                                    component: TargetNew,
                                },
                                {
                                    path: 'edit/:id',
                                    name: i18n.t('edit'),
                                    component: TargetEdit,
                                },
                                {
                                    path: 'view/:targetId',
                                    name: i18n.t('targets.view'),
                                    component: TargetView,
                                    children: [
                                        {
                                            path: 'tags',
                                            name: i18n.t('targets.tags'),
                                            component: LinkTags,
                                        },
                                        {
                                            path: 'checkin',
                                            name: i18n.t('checkIn.checkIn'),
                                            component: CheckIn,
                                        },
                                        {
                                            path: 'checkin/:checkinId',
                                            name: i18n.t('checkIn.checkIn'),
                                            component: CheckIn,
                                        },
                                        {
                                            path: 'checkin-simulated',
                                            name: i18n.t('checkIn.checkIn'),
                                            component: CheckInSimulated,
                                        },
                                        {
                                            path: 'retroactive/:expectationId',
                                            name: i18n.t('checkIn.retroactive'),
                                            component: CheckIn,
                                        },
                                        {
                                            path: 'suggest',
                                            name: i18n.t('targets.suggestGoals'),
                                            component: SuggestGoal,
                                        },
                                        {
                                            path: 'focal-point',
                                            name: i18n.t('targets.focalPoint'),
                                            component: FocalPoint,
                                        },
                                        {
                                            path: 'automatic-calculation',
                                            name: i18n.t('targets.automaticCalculation'),
                                            component: AutomaticCalculation,
                                        },
                                        {
                                            path: 'expectation',
                                            name: i18n.t('targets.expectation'),
                                            component: Expectation,
                                        },
                                        {
                                            path: 'checkin-projection/:checkinId',
                                            name: i18n.t('targets.checkIn'),
                                            component: CheckInProjection,
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            path: 'ranges',
                            name: i18n.t('ranges.ranges'),
                            redirect: 'ranges/list',
                            component: Ranges,
                            children: [
                                {
                                    path: 'list',
                                    name: i18n.t('list'),
                                    component: RangesList,
                                },
                                {
                                    path: 'new',
                                    name: i18n.t('new'),
                                    component: RangeNew,
                                },
                                {
                                    path: 'edit/:id',
                                    name: i18n.t('edit'),
                                    component: RangeEdit,
                                },
                            ],
                        },
                        {
                            path: 'units-of-measurement',
                            name: i18n.t('unitsOfMeasurement.unitsOfMeasurement'),
                            redirect: 'units-of-measurement/list',
                            component: UnitsOfMeasurement,
                            children: [
                                {
                                    path: 'list',
                                    name: i18n.t('list'),
                                    component: UnitsOfMeasurementList,
                                },
                                {
                                    path: 'new',
                                    name: i18n.t('new'),
                                    component: UnitOfMeasurementNew,
                                },
                                {
                                    path: 'edit/:id',
                                    name: i18n.t('edit'),
                                    component: UnitOfMeasurementEdit,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: 'v2/okr',
                    name: 'OKR',
                    component: OKR,
                    children: [
                        {
                            path: 'all-okrs',
                            component: AllOkrs,
                            children: [
                                {
                                    path: 'objectiveId/:objectiveId/resultId/:resultId/checkin',
                                    name: 'Check-In',
                                    component: () => import('@/views/okr/check-in'),
                                },
                                {
                                    path: 'objectiveId/:objectiveId/resultId/:resultId/edit',
                                    name: i18n.t('okr.editKeyResult'),
                                    component: OKRKeyResultEdit,
                                },
                                {
                                    path: 'objective/:objectiveId/edit',
                                    name: i18n.t('objectives.editObjective'),
                                    component: OKRObjectiveEdit,
                                },
                                {
                                    path: 'objective/:objectiveId/request',
                                    name: i18n.t('objectives.editObjective'),
                                    component: OKRObjectiveCollaboration,
                                },

                                {
                                    path: 'objectiveId/:objectiveId/resultId/:resultId/move',
                                    name: i18n.t('okr.moveKeyResult'),
                                    component: OKRKeyResultMove,
                                },
                                {
                                    path: 'objective/:objectiveId/duplicate',
                                    name: i18n.t('okr.modal.objective.duplicate'),
                                    component: OKRObjectiveDuplicate,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: 'v2/feedbacks',
                    name: 'Feedbacks',
                    component: Feedbacks,
                    redirect: 'v2/feedbacks/list',
                    children: [
                        {
                            path: 'list',
                            component: FeedbacksList,
                            children: [
                                {
                                    path: 'request',
                                    name: i18n.t('profile.feedback.request'),
                                    component: RequestFeedbackModal,
                                },
                                {
                                    path: 'requestAdmin',
                                    name: i18n.t('profile.feedback.request'),
                                    component: RequestAdminFeedbackModal,
                                },
                                {
                                    path: 'send',
                                    name: i18n.t('profile.feedback.send'),
                                    component: SendFeedbackModal,
                                },
                            ],
                        },
                        {
                            path: 'templates',
                            name: 'Templates',
                            component: TemplatesList,
                            beforeEnter: (to, from, next) => {
                                return shallPass(next, true)
                            },
                            children: [
                                {
                                    name: i18n.t('new'),
                                    component: NewTemplate,
                                    path: 'new',
                                },
                                {
                                    name: i18n.t('edit'),
                                    component: EditTemplate,
                                    path: 'edit/:id',
                                },
                            ],
                        },
                    ],
                },
                {
                    name: i18n.t('values.moduleName'),
                    path: 'v2/values',
                    component: Values,
                    redirect: 'v2/values/evaluations',
                    children: [
                        {
                            name: i18n.t('values.evaluate'),
                            path: 'evaluations',
                            component: EvaluationsList,
                            children: [
                                {
                                    path: 'spontaneous-evaluation',
                                    name: i18n.t('values.spontaneousEvaluation'),
                                    component: SendSpontaneousEvaluationModal,
                                },
                            ],
                        },
                        {
                            name: i18n.t('values.valuers'),
                            path: 'valuers/:userId',
                            component: UserNetworks,
                        },
                        {
                            name: 'Relatório',
                            path: 'questionnaire/:id/report',
                            component: EvaluationsReport,
                            beforeEnter: (to, from, next) => {
                                return shallPass(next, true)
                            },
                        },
                        {
                            path: 'networks',
                            component: () => toV1('/values/networks'),
                        },
                        {
                            path: 'subjects',
                            component: () => toV1('/values/subjects'),
                        },
                        {
                            path: 'scales',
                            component: () => toV1('/values/scales'),
                        },
                        {
                            path: 'questionnaires',
                            component: () => toV1('/values/questionnaires'),
                        },
                        {
                            path: 'categories',
                            component: () => toV1('/values/categories'),
                        },
                        {
                            path: 'evaluation',
                            component: () => toV1('/values/evaluation'),
                        },
                    ],
                },
                {
                    path: 'v2/n-box',
                    name: 'N-Box',
                    component: NBox,
                    redirect: 'v2/n-box/report',
                    beforeEnter: (to, from, next) => {
                        return shallPass(next, true)
                    },
                    children: [
                        {
                            path: 'report',
                            component: NBoxReport,
                        },
                        {
                            path: 'list',
                            name: i18n.t('list'),
                            component: NBoxList,
                        },
                        {
                            path: 'new',
                            name: i18n.t('new'),
                            component: NBoxNew,
                        },
                        {
                            path: 'edit/:id',
                            name: i18n.t('edit'),
                            component: NBoxEdit,
                        },
                    ],
                },
                {
                    path: 'v2/reports',
                    name: i18n.t('reports.reports'),
                    component: {
                        render(c) {
                            return c('router-view')
                        },
                    },
                    children: [
                        {
                            path: 'objectives',
                            component: () => toV1('/reports/objectives'),
                        },
                        {
                            path: 'tags',
                            component: TagReport,
                            name: 'Tags',
                        },
                        {
                            path: 'enps',
                            component: EnpsReport,
                            name: 'eNPS',
                            beforeEnter: (to, from, next) => {
                                return shallPass(next, false, true)
                            },
                        },
                    ],
                },
                {
                    path: 'v2/administrator',
                    component: {
                        render(c) {
                            return c('router-view')
                        },
                    },
                    children: [
                        {
                            path: 'hierarchy',
                            component: () => toV1('/administrator/hierarchy'),
                        },
                        {
                            path: 'mood-pulse',
                            component: MoodPulse,
                            name: i18n.t('moodPulse.moodPulse'),
                            beforeEnter: (to, from, next) => {
                                return shallPass(next, false, true)
                            },
                        },
                        {
                            path: 'mood-pulse/report/:id?',
                            name: i18n.t('moodPulse.report.report'),
                            component: QuizReport,
                            beforeEnter: (to, from, next) => {
                                return shallPass(next, false, true)
                            },
                        },
                        {
                            path: 'occupation-groups',
                            component: () => toV1('/administrator/occupation-groups'),
                        },
                        {
                            path: 'users',
                            component: () => toV1('/administrator/users'),
                        },
                        {
                            path: 'periods',
                            component: () => toV1('/administrator/periods'),
                        },
                        {
                            path: 'tags',
                            component: () => toV1('/administrator/tags'),
                        },
                        {
                            path: 'integrations',
                            component: () => toV1('/administrator/integrations'),
                        },
                        {
                            path: 'customers',
                            component: () => toV1('/administrator/customers'),
                        },
                        {
                            path: 'committees',
                            name: i18n.t('committees.committees'),
                            redirect: 'committees/list',
                            component: Committee,
                            beforeEnter: (to, from, next) => {
                                return shallPass(next, false, true)
                            },
                            children: [
                                {
                                    path: 'list',
                                    name: i18n.t('list'),
                                    component: CommitteeList,
                                },
                                {
                                    path: 'new',
                                    name: i18n.t('new'),
                                    component: CommitteeNew,
                                },
                                {
                                    path: 'edit/:id',
                                    name: i18n.t('edit'),
                                    component: CommitteeEdit,
                                },
                            ],
                        },
                        {
                            path: 'enps',
                            component: Enps,
                            name: i18n.t('eNPS'),
                            beforeEnter: (to, from, next) => {
                                return shallPass(next, false, true)
                            },
                            children: [
                                {
                                    path: 'new',
                                    name: i18n.t('new'),
                                    component: NewEnpsResearch,
                                },
                                {
                                    path: 'edit/:id',
                                    name: i18n.t('edit'),
                                    component: EditEnpsResearch,
                                },
                                {
                                    path: 'duplicate/:id',
                                    name: i18n.t('duplicate'),
                                    component: Duplicate,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: 'v2/help-center',
                    component: () => window.open('https://coblue.movidesk.com/kb/'),
                },
            ],
        },
        {
            path: '/v2/login',
            name: 'Login',
            component: Login,
        },
        {
            path: '/v2/login/cognito-ad',
            name: 'LoginCognitoAD',
            component: LoginCognitoAD,
        },
        {
            path: '/v2/login/forgot-password',
            name: 'ForgotPassword',
            component: ForgotPassword,
        },
        {
            path: '/v2/login/create-password/:token?',
            name: 'CreatePassword',
            component: CreatePassword,
        },
        {
            path: '/v2/terms-of-use',
            name: 'TermsOfUse',
            component: TermsOfUse,
        },
        {
            path: '/v2/login/update-password/:token?',
            name: 'CreatePassword',
            component: CreatePassword,
            props: {
                expirePassword: true,
            },
        },
        {
            path: '/v2/pages/error/:status',
            name: 'Error',
            component: Error,
        },
    ],
})

router.beforeEach((to, from, next) => {
    const matched = to.path.match(/\/v2\/login\/(?!update-password).+/gi)
    if (matched && matched.length) {
        if (tokenStorage.getAccessToken()) {
            router.push({ name: 'Home' })
        } else {
            next()
        }
    } else next()
})

export default router
